import {
  Alert,
  AlertColor,
  Snackbar,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Container,
  Content,
  ContentBody,
  ContentBodyLeft,
  ContentBodyRight,
  ContentInput,
  ContentInputProfilePhoto,
  ContentInputProfilePhotoIcon,
  ContentTitle,
  Title,
  VersionList,
  FieldContainer,
  FieldLabel,
  TagsContainer,
  Tag,
  RemoveButton,
  FolderFieldRowFull,
  InputFieldSimple,
  InputLabel,
} from "./styles";
import { useParams } from "react-router-dom";
import { UserPermissions } from "../../../App";
import CustomDropdown from "../../../Components/CustomDropdown";
import RenderVersion from "./Version";

interface Props {
  userPermissions: UserPermissions;
}

interface ExtensionData {
  name: string;
  description: string;
  latestVersion?: string;
  logo?: string;
  tags?: { value: string; color: string }[];
  publisherType?: string;
}

const ViewExtension: React.FC<Props> = ({ userPermissions }) => {
  const { extensionID } = useParams();
  const [username, setUsername] = useState("");
  const [tagInput, setTagInput] = useState<string>("");

  const [showStatus, setShowStatus] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [statusSeverity, setStatusSeverity] =
    useState<AlertColor>("error");
  const handleCloseStatus = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setShowStatus(false);
  };

  const [extensionData, setExtensionData] = useState<ExtensionData>({
    name: "",
    description: " ",
    latestVersion: undefined,
    logo: undefined,
    tags: [],
    publisherType: "org",
  });

  const [ownerOptions, setOwnerOptions] = useState<any[]>([
    /*{ label: "ResearchX", type: 'user', value: 'research_x', checked: false }*/
  ]);
  const [publisherOrg, setPublisherOrg] = useState<string | undefined>(undefined);
  const publisherOptions = [
    { label: "Organization", value: "org" },
    { label: "User", value: "user" },
  ];

  const onPublisherTypeChange = (values: string[]) => {
    const selected = publisherOptions.find(option => values.includes(option.value));

    setExtensionData(prev => ({
      ...prev,
      publisherType: selected?.value,
    }));
  };

  const getPossibleOwnerList = async () => {
    const response = await fetch("/api/v1/software/getCreationProperties", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })

    const data = await response.json();

    if (data.success !== true) return;

    const dataToSet = data.possibleOwnerList.map((val: any) => {
      return {
        label: val.name,
        type: val.type,
        value: val.id,
      };
    });

    setOwnerOptions(dataToSet);

    return dataToSet;
  };
  const [versions, setVersions] = useState<
    Array<{
      description: string;
      type: string;
      version: string;
      status: "active" | "unlisted" | "deprecated" | "revoked";
    }>
  >([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleSquareClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (png, jpg, etc.)');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const fullBase64String = reader.result as string;

        setExtensionData((prev) => ({
          ...prev,
          logo: fullBase64String,
        }));
      };

      reader.readAsDataURL(file);
    }
  };

  const changeVar = (varName: string, value: string) => {
    setExtensionData((prev) => ({
      ...prev,
      [varName]: value,
    }));
  };

  const removeTag = (index: number) => {
    setExtensionData(prev => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index)
    }));
  };

  const handleTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedInput = tagInput.trim();

      if (trimmedInput && !extensionData.tags?.some(tag => tag.value === trimmedInput)) {
        setExtensionData(prev => ({
          ...prev,
          tags: [
            ...(prev.tags || []),
            {
              value: trimmedInput,
              color: '#2E933C'
            }
          ]
        }));
        setTagInput("");
      }
    }
  };

  const getExtension = async () => {
    const response = await fetch(`/api/v1/extension/get/${extensionID}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      }
    })

    const data = await response.json();

    if (data?.success === true) {
      delete data.success;

      const owners = await getPossibleOwnerList();

      const dataToSet: ExtensionData = {
        name: data.extension.name,
        description: data.extension.description,
        latestVersion: data.extension.latestVersion,
        logo: data.extension.logo,
        tags: data.extension.tags?.map((tag: string) => ({ value: tag, color: '#2E933C' })) || [],
        publisherType: data.extension.publisher.type || "org",
      }
      setExtensionData(dataToSet);

      setPublisherOrg(owners.find((option: any) => option.value === data.extension.publisher.id)?.value);
    }
  };

  const editExtension = async (
    _id: string,
    _name: string,
    _description: string,
    _logo: string,
    _publisherType: string,
    _publisherId: any,
    _tags: any
  ) => {
    console.log(`Editing extension ${_id}`);

    const dataToSend = {
      id: _id,
      name: _name,
      description: _description ? _description : " ",
      publisherType: _publisherType,
      publisherId: _publisherType === "user" ? username : _publisherId,
      logo: _logo,
      tags: _tags,
    };

    console.log(dataToSend);
    fetch("/api/v1/extension/edit", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: _id,
        name: _name,
        description: _description ? _description : " ",
        publisherType: _publisherType,
        publisherId: _publisherId,
        logo: _logo,
        tags: _tags,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success === true) {
          setStatusMsg(`Changes to '${_name}' were saved!`);
          setStatusSeverity("success");
          setShowStatus(true);
          getExtension();
          setTimeout(() => {
            setShowStatus(false);
          }, 5000);
          return data.success;
        } else {
          setStatusMsg(data.reason);
          setStatusSeverity("error");
          setShowStatus(true);
          setTimeout(() => {
            setShowStatus(false);
          }, 5000);
        }
      });
  };

  const getUsername = async () => {
    const response = await fetch("/api/v1/account/myAccount", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    const data = await response.json();
    if (data.success === true) {
      setUsername(data.username);
    }
  };

  const isUserPublisherTypeSelected = useMemo(() => {
    return extensionData.publisherType === "user";
  }, [extensionData.publisherType]);

  const getVersions = async () => {
    if (!extensionID) return;

    try {
      const response = await fetch("/api/v1/extension/version/list", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: extensionID }),
      });

      const data = await response.json();

      if (data.success === true && data.versions) {
        setVersions(data.versions);
      }
    } catch (err) {
      console.log("Failed to fetch versions:", err);
    }
  };

  useEffect(() => {
    getUsername();
    getExtension();
    getVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <>
        <Title>
          View Extension
          <Button
            onClick={() => {
              editExtension(
                extensionID!,
                extensionData.name,
                extensionData.description,
                extensionData.logo || "",
                extensionData.publisherType || "org",
                extensionData.publisherType === "user" ? username : publisherOrg,
                extensionData.tags?.map(tag => tag.value)
              );
            }}
          >
            Save changes
          </Button>
        </Title>

        <Content>
          <ContentTitle>{extensionID}</ContentTitle>

          <ContentBody style={{ padding: "10px" }}>
            <ContentBodyLeft>
              <ContentInput
                label="Name"
                variant="standard"
                value={extensionData.name}
                onChange={(ev) => changeVar("name", ev.target.value)}
              />

              <ContentInput
                id="standard-multiline-static"
                label="Description"
                multiline
                rows={2}
                variant="standard"
                value={extensionData.description}
                onChange={(ev) => changeVar("description", ev.target.value)}
              />

              {/* Tags */}
              <FieldContainer style={{ marginTop: "1rem" }}>
                <FieldLabel>Tags</FieldLabel>
                {
                  extensionData.tags && extensionData.tags.length > 0 && (
                    <TagsContainer>
                      {extensionData.tags?.map((tag, index) => (
                        <Tag key={index} color={tag.color}>
                          {tag.value}
                          <RemoveButton onClick={() => removeTag(index)}>
                            ×
                          </RemoveButton>
                        </Tag>
                      ))}
                    </TagsContainer>
                  )
                }
                <FolderFieldRowFull style={{ flexWrap: "wrap", gap: "8px", alignItems: "flex-start" }}>
                  <InputFieldSimple
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagInput}
                    placeholder="Type and press Enter to add tag"
                    style={{ flex: "1 1 200px", minWidth: "200px" }}
                  />
                </FolderFieldRowFull>
              </FieldContainer>

              <div style={{ display: 'flex', flexDirection: 'column', gap: "1rem", marginTop: "1rem" }}>
                <InputLabel>Publisher</InputLabel>
                <span style={{ display: 'flex', gap: "1rem", width: "100%" }}>
                  {/* Publisher Type */}
                  <CustomDropdown
                    isMulti={false}
                    options={publisherOptions}
                    selectedValues={extensionData.publisherType ? [extensionData.publisherType] : []}
                    onChange={onPublisherTypeChange}
                    style={{ height: '100%', width: '30%', border: `1px solid #374957` }}
                  />

                  {/* Publisher ID */}
                  <CustomDropdown
                    isMulti={false}
                    disabled={isUserPublisherTypeSelected}
                    options={ownerOptions.map(owner => ({ label: owner.label, value: owner.value }))}
                    selectedValues={isUserPublisherTypeSelected ? [username] : [publisherOrg || ""]}
                    onChange={(values) => setPublisherOrg(values[0])}
                    style={{ height: '100%', width: '70%', border: `1px solid #374957` }}
                  />
                </span>
              </div>
            </ContentBodyLeft>

            <ContentBodyRight>
              <ContentInputProfilePhoto onClick={handleSquareClick}>
                <img src={extensionData.logo} alt="Click here to upload the extension logo" />
                <ContentInputProfilePhotoIcon />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </ContentInputProfilePhoto>
            </ContentBodyRight>

            <VersionList
              container
              spacing={1}
              style={{
                height: "auto"
              }}
            >
              {versions.map((version: any, index) => (
                <RenderVersion
                  key={`version-${index}`}
                  extensionID={extensionID!}
                  version={version}
                  isLatest={version.version === extensionData.latestVersion}
                  onRefresh={getVersions}
                />
              ))}
            </VersionList>
          </ContentBody>
        </Content>

        <Snackbar
          open={showStatus}
          autoHideDuration={6000}
          onClose={handleCloseStatus}
        >
          <Alert
            onClose={handleCloseStatus}
            severity={statusSeverity}
            sx={{ width: "100%" }}
          >
            {statusMsg}
          </Alert>
        </Snackbar>
      </>
    </Container>
  );
};

export default ViewExtension;
